// frontend/src/lib/gravityFramework.js

/**
 * AI Fine Protector™ — Gravity Framework v3 (Pass 1, Hardened)
 *
 * Purpose:
 * - Provide a standalone, deterministic reference framework for the future Layer 2
 *   Governance Adjusted Exposure Range.
 * - This file does NOT modify the existing exposure engine.
 * - This file does NOT change Reports, PDFs, Universal Audit, Assessment,
 *   Remediation Hub, Auth, Stripe, Supabase, or routing.
 *
 * Product/legal positioning:
 * - Outputs are governance and planning indicators.
 * - Outputs are not legal advice, legal conclusions, compliance certifications,
 *   or regulatory guarantees.
 *
 * Safety design:
 * - Public helpers return human-readable labels and rationale.
 * - Internal numeric multipliers are intentionally separated from public labels.
 * - Unknown or missing inputs always fall back to conservative baseline profiles.
 * - Layer 2 guardrails are bounded so gravity can shape outcomes but not dominate them.
 */

const VERSION = "gravity.framework.v3.pass1.hardened.v2";

const PUBLIC_MODEL_NOTICE =
  "Governance-adjusted exposure is a planning estimate based on stated inputs, configured jurisdiction profiles, evidence readiness, remediation progress, user impact, and data sensitivity. It is not legal advice, a legal conclusion, or a regulatory guarantee.";

const INTERNAL_ONLY_NOTICE =
  "Internal model value. Do not display this raw value in customer-facing UI or PDFs.";

const LABELS = Object.freeze({
  enforcement: Object.freeze({
    VERY_HIGH: "Very High",
    HIGH: "High",
    MODERATE_HIGH: "Moderate High",
    MODERATE: "Moderate",
    MODERATE_LOW: "Moderate Low",
    LOW: "Low",
    BASELINE: "Baseline",
  }),
  dataSensitivity: Object.freeze({
    STANDARD: "Standard",
    ELEVATED: "Elevated",
    HIGH: "High",
    CRITICAL: "Critical",
  }),
  userImpact: Object.freeze({
    LOW: "Low",
    MODERATE: "Moderate",
    HIGH: "High",
    ENTERPRISE_SCALE: "Enterprise Scale",
  }),
  governance: Object.freeze({
    WEAK: "Weak",
    DEVELOPING: "Developing",
    ESTABLISHED: "Established",
    ADVANCED: "Advanced",
  }),
  evidence: Object.freeze({
    LIMITED: "Limited",
    DEVELOPING: "Developing",
    STRONG: "Strong",
    AUDIT_READY: "Audit Ready",
  }),
  remediation: Object.freeze({
    MINIMAL: "Minimal",
    IN_PROGRESS: "In Progress",
    SUBSTANTIAL: "Substantial",
    NEAR_COMPLETE: "Near Complete",
  }),
  confidence: Object.freeze({
    LOW: "Low",
    MODERATE: "Moderate",
    MEDIUM_HIGH: "Medium High",
    HIGH: "High",
  }),
});

/**
 * Internal-only model multipliers.
 *
 * Guardrail:
 * - These should not be shown to customers.
 * - UI/PDF should show labels such as "High" or "Moderate High".
 * - Future overlay engines may import these through the explicit internal helper.
 */
const INTERNAL_MULTIPLIERS = Object.freeze({
  enforcement: Object.freeze({
    VERY_HIGH: 1.2,
    HIGH: 1.1,
    MODERATE_HIGH: 1.05,
    MODERATE: 1,
    MODERATE_LOW: 0.95,
    LOW: 0.9,
    BASELINE: 1,
  }),
  dataSensitivity: Object.freeze({
    STANDARD: 1,
    ELEVATED: 1.04,
    HIGH: 1.08,
    CRITICAL: 1.12,
  }),
  userImpact: Object.freeze({
    LOW: 0.98,
    MODERATE: 1,
    HIGH: 1.03,
    ENTERPRISE_SCALE: 1.06,
  }),
});

/**
 * Layer 2 hard guardrails for future overlay implementation.
 * These values are included here so the future engine and UI/PDF copy can rely on a
 * single source of truth. They do not affect production until imported by a future pass.
 */
const LAYER_2_GUARDRAILS = Object.freeze({
  overall_adjustment_floor_ratio: 0.7,
  overall_adjustment_ceiling_ratio: 1.2,
  gravity_only_min_ratio: 0.88,
  gravity_only_max_ratio: 1.2,
  max_reduction_without_strong_evidence_ratio: 0.9,
  sensitivity_minimum_ratio_by_profile: Object.freeze({
    STANDARD: 0.7,
    ELEVATED: 0.75,
    HIGH: 0.8,
    CRITICAL: 0.85,
  }),
  jurisdiction_max_reduction_ratio_by_profile: Object.freeze({
    VERY_HIGH: 0.85,
    HIGH: 0.8,
    MODERATE_HIGH: 0.75,
    MODERATE: 0.72,
    MODERATE_LOW: 0.7,
    LOW: 0.7,
    BASELINE: 0.75,
  }),
});

const FACTOR_WEIGHTS = Object.freeze({
  governance_maturity: 0.3,
  evidence_readiness: 0.25,
  remediation_progress: 0.15,
  jurisdiction_enforcement_profile: 0.15,
  data_sensitivity_profile: 0.1,
  user_impact_profile: 0.05,
});

const CONFIDENCE_WEIGHTS = Object.freeze({
  assessment_completeness: 0.35,
  evidence_coverage: 0.35,
  remediation_completeness: 0.2,
  data_quality: 0.1,
});

const JURISDICTION_PROFILES = Object.freeze({
  EU: Object.freeze({
    profile: "VERY_HIGH",
    internal_gravity_score: 100,
    internal_multiplier: 1.2,
    confidence: "HIGH",
    rationale:
      "Mature GDPR enforcement ecosystem, coordinated supervisory authority framework, and sustained regulatory attention.",
    source_basis: "GDPR / EDPB supervisory authority framework",
  }),
  UK: Object.freeze({
    profile: "VERY_HIGH",
    internal_gravity_score: 96,
    internal_multiplier: 1.18,
    confidence: "HIGH",
    rationale:
      "Established ICO enforcement framework, mature privacy regime, and active AI governance focus.",
    source_basis: "UK GDPR / Data Protection Act / ICO enforcement framework",
  }),
  CA: Object.freeze({
    profile: "VERY_HIGH",
    internal_gravity_score: 100,
    internal_multiplier: 1.2,
    confidence: "HIGH",
    rationale:
      "Dedicated California privacy regulator, mature enforcement infrastructure, and strong privacy policy momentum.",
    source_basis: "CCPA / CPRA / California Privacy Protection Agency enforcement framework",
  }),
  NY: Object.freeze({
    profile: "HIGH",
    internal_gravity_score: 90,
    internal_multiplier: 1.14,
    confidence: "HIGH",
    rationale:
      "Active attorney general enforcement environment and significant financial-sector and consumer protection relevance.",
    source_basis: "New York SHIELD Act / NY Attorney General consumer protection enforcement environment",
  }),
  IL: Object.freeze({
    profile: "HIGH",
    internal_gravity_score: 88,
    internal_multiplier: 1.13,
    confidence: "HIGH",
    rationale:
      "Material biometric privacy litigation environment and significant private-action pressure.",
    source_basis: "Illinois BIPA / consumer privacy litigation environment",
  }),
  WA: Object.freeze({
    profile: "HIGH",
    internal_gravity_score: 84,
    internal_multiplier: 1.11,
    confidence: "HIGH",
    rationale:
      "Strong health-data privacy focus and elevated sensitivity around consumer health information.",
    source_basis: "Washington My Health My Data Act / Consumer Protection Act framework",
  }),
  FL: Object.freeze({
    profile: "HIGH",
    internal_gravity_score: 80,
    internal_multiplier: 1.09,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Large consumer market with enhanced children-related privacy exposure and active consumer protection relevance.",
    source_basis: "Florida Digital Bill of Rights / consumer protection enforcement framework",
  }),
  MD: Object.freeze({
    profile: "HIGH",
    internal_gravity_score: 82,
    internal_multiplier: 1.1,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Strong modern privacy statute with heightened data minimization and sensitive-data obligations.",
    source_basis: "Maryland Online Data Privacy Act / Maryland consumer protection framework",
  }),
  CO: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 72,
    internal_multiplier: 1.05,
    confidence: "HIGH",
    rationale:
      "Mature comprehensive privacy framework with credible consumer protection enforcement pathway.",
    source_basis: "Colorado Privacy Act / Colorado Consumer Protection Act framework",
  }),
  NJ: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 74,
    internal_multiplier: 1.06,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Comprehensive privacy regime with consumer fraud enforcement relevance and material state-market importance.",
    source_basis: "New Jersey Data Privacy Act / Consumer Fraud Act enforcement framework",
  }),
  MA: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 72,
    internal_multiplier: 1.05,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Established data-security expectations and consumer protection enforcement history.",
    source_basis: "Massachusetts data security / consumer protection enforcement framework",
  }),
  CT: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 70,
    internal_multiplier: 1.04,
    confidence: "HIGH",
    rationale:
      "Comprehensive privacy framework with unfair trade practices enforcement basis.",
    source_basis: "Connecticut Data Privacy Act / CUTPA enforcement framework",
  }),
  OR: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 70,
    internal_multiplier: 1.04,
    confidence: "HIGH",
    rationale:
      "Comprehensive consumer privacy framework and attorney general enforcement pathway.",
    source_basis: "Oregon Consumer Privacy Act",
  }),
  TX: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 74,
    internal_multiplier: 1.06,
    confidence: "HIGH",
    rationale:
      "Large market and attorney general enforcement structure, with significant consumer and business impact potential.",
    source_basis: "Texas Data Privacy and Security Act",
  }),
  DE: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 68,
    internal_multiplier: 1.03,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Comprehensive privacy framework with consumer protection enforcement basis.",
    source_basis: "Delaware Personal Data Privacy Act",
  }),
  MN: Object.freeze({
    profile: "MODERATE_HIGH",
    internal_gravity_score: 72,
    internal_multiplier: 1.05,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Modern comprehensive privacy law and attorney general enforcement pathway with meaningful penalty authority.",
    source_basis: "Minnesota Consumer Data Privacy Act / Minnesota Attorney General enforcement materials",
  }),
  VA: Object.freeze({
    profile: "MODERATE",
    internal_gravity_score: 60,
    internal_multiplier: 1.0,
    confidence: "HIGH",
    rationale:
      "Comprehensive privacy statute with attorney general enforcement, but comparatively less aggressive public enforcement signal.",
    source_basis: "Virginia Consumer Data Protection Act",
  }),
  RI: Object.freeze({
    profile: "MODERATE",
    internal_gravity_score: 58,
    internal_multiplier: 0.99,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Privacy framework present, with developing enforcement maturity.",
    source_basis: "Rhode Island Data Transparency and Privacy Protection Act",
  }),
  NH: Object.freeze({
    profile: "MODERATE",
    internal_gravity_score: 57,
    internal_multiplier: 0.99,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Newer comprehensive privacy regime with developing enforcement track record.",
    source_basis: "New Hampshire Data Privacy Act / consumer protection framework",
  }),
  TN: Object.freeze({
    profile: "MODERATE",
    internal_gravity_score: 60,
    internal_multiplier: 1.0,
    confidence: "MEDIUM_HIGH",
    rationale:
      "Comprehensive privacy framework with emerging implementation and enforcement posture.",
    source_basis: "Tennessee Information Protection Act",
  }),
  IN: Object.freeze({
    profile: "MODERATE_LOW",
    internal_gravity_score: 50,
    internal_multiplier: 0.95,
    confidence: "MEDIUM",
    rationale:
      "Comprehensive privacy framework exists, but enforcement ecosystem remains comparatively early-stage.",
    source_basis: "Indiana Consumer Data Protection Act",
  }),
  KY: Object.freeze({
    profile: "MODERATE_LOW",
    internal_gravity_score: 48,
    internal_multiplier: 0.94,
    confidence: "MEDIUM",
    rationale:
      "Comprehensive privacy framework exists, with developing enforcement signals.",
    source_basis: "Kentucky Consumer Data Protection Act",
  }),
  MT: Object.freeze({
    profile: "MODERATE_LOW",
    internal_gravity_score: 50,
    internal_multiplier: 0.95,
    confidence: "MEDIUM",
    rationale: "Privacy framework exists, with still-developing enforcement history.",
    source_basis: "Montana Consumer Data Privacy Act",
  }),
  UT: Object.freeze({
    profile: "MODERATE_LOW",
    internal_gravity_score: 46,
    internal_multiplier: 0.93,
    confidence: "HIGH",
    rationale:
      "Comprehensive privacy framework exists, but with comparatively lighter regulatory posture.",
    source_basis: "Utah Consumer Privacy Act",
  }),
  IA: Object.freeze({
    profile: "LOW",
    internal_gravity_score: 38,
    internal_multiplier: 0.9,
    confidence: "MEDIUM",
    rationale: "Newer privacy ecosystem with limited public enforcement history.",
    source_basis: "Iowa Consumer Data Protection Act",
  }),
  NE: Object.freeze({
    profile: "LOW",
    internal_gravity_score: 38,
    internal_multiplier: 0.9,
    confidence: "MEDIUM",
    rationale: "Newer privacy ecosystem with limited public enforcement history.",
    source_basis: "Nebraska Data Privacy Act",
  }),
});

const DATA_SENSITIVITY_KEYWORDS = Object.freeze({
  CRITICAL: Object.freeze([
    "children health",
    "child health",
    "minors health",
    "children's health",
    "biometrics at scale",
    "biometric at scale",
    "sensitive ai",
    "genetic",
    "precise geolocation",
  ]),
  HIGH: Object.freeze([
    "health",
    "healthcare",
    "medical",
    "patient",
    "clinic",
    "hospital",
    "hipaa",
    "children",
    "minor",
    "student",
    "education",
    "school",
    "biometric",
    "face recognition",
    "facial recognition",
  ]),
  ELEVATED: Object.freeze([
    "financial",
    "bank",
    "lending",
    "credit",
    "insurance",
    "employment",
    "hr",
    "hiring",
    "consumer platform",
    "marketplace",
    "advertising",
    "marketing",
  ]),
});

const EXPOSURE_DRIVER_LABELS = Object.freeze({
  WEAK_GOVERNANCE: "Weak governance maturity",
  HIGH_ENFORCEMENT: "High-enforcement jurisdiction profile",
  HIGH_DATA_SENSITIVITY: "High data sensitivity",
  LARGE_USER_IMPACT: "Large user impact volume",
  LIMITED_EVIDENCE: "Limited evidence readiness",
  LIMITED_REMEDIATION: "Incomplete remediation progress",
  MULTI_JURISDICTION: "Multi-jurisdiction regulatory exposure",
  FALLBACK_STATE: "Fallback-state model uncertainty",
  FEDERAL_OVERLAY: "Federal overlay exposure",
  HIGH_RISK_SECTOR: "High-risk sector indicators",
});

const RISK_REDUCTION_DRIVER_LABELS = Object.freeze({
  STRONG_GOVERNANCE: "Strong governance maturity",
  STRONG_EVIDENCE: "Strong evidence readiness",
  REMEDIATION_PROGRESS: "Meaningful remediation progress",
  LOWER_SENSITIVITY: "Lower data sensitivity profile",
  LOWER_IMPACT: "Lower user impact volume",
  AUDIT_READY_EVIDENCE: "Audit-ready evidence library",
});

function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  const n = asNumber(value, min);
  return Math.max(min, Math.min(max, n));
}

function upper(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStateOrRegime(value) {
  const raw = upper(value).replace(/\s+/g, "_").replace(/-/g, "_");
  if (!raw) return "";
  if (raw === "US_FED" || raw === "US_FEDERAL" || raw === "US_FEDERAL_OVERLAY") return "US_FED";
  if (raw === "EU" || raw === "EUROPEAN_UNION" || raw === "EEA") return "EU";
  if (raw === "UK" || raw === "UNITED_KINGDOM" || raw === "GB" || raw === "GREAT_BRITAIN") return "UK";
  const stateMatch = raw.match(/^US_([A-Z]{2})$/);
  if (stateMatch?.[1]) return stateMatch[1];
  if (/^[A-Z]{2}$/.test(raw)) return raw;
  return raw;
}

function publicProfileResult({ profile, labelMap, fallbackProfile, extra = {} }) {
  const safeProfile = labelMap[profile] ? profile : fallbackProfile;
  return {
    profile: safeProfile,
    label: labelMap[safeProfile],
    ...extra,
  };
}

export function getGravityFrameworkVersion() {
  return VERSION;
}

export function getPublicModelNotice() {
  return PUBLIC_MODEL_NOTICE;
}

export function getLayer2Guardrails() {
  return {
    ...LAYER_2_GUARDRAILS,
    notice: "Guardrails are used to keep governance-adjusted exposure outcomes within plausible planning boundaries.",
  };
}

export function getFactorWeights() {
  return { ...FACTOR_WEIGHTS };
}

export function getConfidenceWeights() {
  return { ...CONFIDENCE_WEIGHTS };
}

/**
 * Public jurisdiction profile helper.
 * Does not return raw multipliers.
 */
export function getJurisdictionEnforcementProfile(jurisdiction) {
  const key = normalizeStateOrRegime(jurisdiction);
  const configured = JURISDICTION_PROFILES[key];

  if (configured) {
    return {
      jurisdiction: key,
      configured: true,
      profile: configured.profile,
      label: LABELS.enforcement[configured.profile] || LABELS.enforcement.BASELINE,
      confidence: configured.confidence || "LOW",
      confidence_label: LABELS.confidence[configured.confidence] || configured.confidence || LABELS.confidence.LOW,
      rationale: configured.rationale,
      source_basis: configured.source_basis,
      customer_visible: true,
    };
  }

  return {
    jurisdiction: key || "UNKNOWN",
    configured: false,
    profile: "BASELINE",
    label: LABELS.enforcement.BASELINE,
    confidence: "LOW",
    confidence_label: LABELS.confidence.LOW,
    rationale:
      "No jurisdiction-specific enforcement gravity profile is currently configured. Baseline treatment is used.",
    source_basis: "AI Fine Protector baseline gravity framework",
    customer_visible: true,
  };
}

/**
 * Internal helper for future overlay engine only.
 * UI and PDF code should not display this raw numeric output.
 */
export function getInternalJurisdictionEnforcementMultiplier(jurisdiction) {
  const key = normalizeStateOrRegime(jurisdiction);
  const configured = JURISDICTION_PROFILES[key];
  const profile = getJurisdictionEnforcementProfile(jurisdiction).profile;
  const defaultMultiplier = INTERNAL_MULTIPLIERS.enforcement[profile] || 1;
  const configuredMultiplier = Number(configured?.internal_multiplier);

  return {
    multiplier: Number.isFinite(configuredMultiplier)
      ? clamp(configuredMultiplier, LAYER_2_GUARDRAILS.gravity_only_min_ratio, LAYER_2_GUARDRAILS.gravity_only_max_ratio)
      : defaultMultiplier,
    profile,
    internal_gravity_score: Number.isFinite(Number(configured?.internal_gravity_score))
      ? clamp(configured.internal_gravity_score, 0, 100)
      : null,
    visibility: "INTERNAL_ONLY",
    notice: INTERNAL_ONLY_NOTICE,
  };
}

export function classifyUserImpactProfile(peopleImpactedPerMonth) {
  const people = Math.max(0, asNumber(peopleImpactedPerMonth, 0));

  if (people >= 100_000) {
    return publicProfileResult({
      profile: "ENTERPRISE_SCALE",
      labelMap: LABELS.userImpact,
      fallbackProfile: "LOW",
      extra: { people_impacted_per_month: people },
    });
  }
  if (people >= 10_000) {
    return publicProfileResult({
      profile: "HIGH",
      labelMap: LABELS.userImpact,
      fallbackProfile: "LOW",
      extra: { people_impacted_per_month: people },
    });
  }
  if (people >= 1_000) {
    return publicProfileResult({
      profile: "MODERATE",
      labelMap: LABELS.userImpact,
      fallbackProfile: "LOW",
      extra: { people_impacted_per_month: people },
    });
  }
  return publicProfileResult({
    profile: "LOW",
    labelMap: LABELS.userImpact,
    fallbackProfile: "LOW",
    extra: { people_impacted_per_month: people },
  });
}

export function classifyGovernanceMaturity(scorePct) {
  const score = clamp(scorePct, 0, 100);

  if (score >= 81) {
    return publicProfileResult({ profile: "ADVANCED", labelMap: LABELS.governance, fallbackProfile: "WEAK", extra: { score_pct: score } });
  }
  if (score >= 61) {
    return publicProfileResult({ profile: "ESTABLISHED", labelMap: LABELS.governance, fallbackProfile: "WEAK", extra: { score_pct: score } });
  }
  if (score >= 41) {
    return publicProfileResult({ profile: "DEVELOPING", labelMap: LABELS.governance, fallbackProfile: "WEAK", extra: { score_pct: score } });
  }
  return publicProfileResult({ profile: "WEAK", labelMap: LABELS.governance, fallbackProfile: "WEAK", extra: { score_pct: score } });
}

export function classifyDataSensitivityProfile({ industry, niche, sector, description, assessmentSignals } = {}) {
  const haystack = [industry, niche, sector, description, ...safeArray(assessmentSignals)]
    .map(normalizeText)
    .filter(Boolean)
    .join(" | ");

  if (!haystack) {
    return publicProfileResult({
      profile: "STANDARD",
      labelMap: LABELS.dataSensitivity,
      fallbackProfile: "STANDARD",
      extra: { matched_terms: [], inferred_from_available_inputs: false },
    });
  }

  for (const profile of ["CRITICAL", "HIGH", "ELEVATED"]) {
    const matches = DATA_SENSITIVITY_KEYWORDS[profile].filter((term) => haystack.includes(term));
    if (matches.length) {
      return publicProfileResult({
        profile,
        labelMap: LABELS.dataSensitivity,
        fallbackProfile: "STANDARD",
        extra: { matched_terms: matches, inferred_from_available_inputs: true },
      });
    }
  }

  return publicProfileResult({
    profile: "STANDARD",
    labelMap: LABELS.dataSensitivity,
    fallbackProfile: "STANDARD",
    extra: { matched_terms: [], inferred_from_available_inputs: true },
  });
}

export function classifyEvidenceReadiness({ evidenceCount = 0, supportedControls = 0, totalControls = 0 } = {}) {
  const evidence = Math.max(0, asNumber(evidenceCount, 0));
  const supported = Math.max(0, asNumber(supportedControls, 0));
  const total = Math.max(0, asNumber(totalControls, 0));
  const coverage = total > 0 ? clamp(supported / total, 0, 1) : 0;

  if (evidence >= 20 && coverage >= 0.75) {
    return publicProfileResult({ profile: "AUDIT_READY", labelMap: LABELS.evidence, fallbackProfile: "LIMITED", extra: { evidence_count: evidence, coverage_ratio: coverage } });
  }
  if (evidence >= 8 && coverage >= 0.45) {
    return publicProfileResult({ profile: "STRONG", labelMap: LABELS.evidence, fallbackProfile: "LIMITED", extra: { evidence_count: evidence, coverage_ratio: coverage } });
  }
  if (evidence >= 1 || coverage > 0) {
    return publicProfileResult({ profile: "DEVELOPING", labelMap: LABELS.evidence, fallbackProfile: "LIMITED", extra: { evidence_count: evidence, coverage_ratio: coverage } });
  }
  return publicProfileResult({ profile: "LIMITED", labelMap: LABELS.evidence, fallbackProfile: "LIMITED", extra: { evidence_count: evidence, coverage_ratio: coverage } });
}

export function classifyRemediationProgress({ completeCount = 0, inProgressCount = 0, totalCount = 0 } = {}) {
  const complete = Math.max(0, asNumber(completeCount, 0));
  const inProgress = Math.max(0, asNumber(inProgressCount, 0));
  const total = Math.max(0, asNumber(totalCount, 0));
  const completion = total > 0 ? clamp(complete / total, 0, 1) : 0;

  if (total > 0 && completion >= 0.85) {
    return publicProfileResult({ profile: "NEAR_COMPLETE", labelMap: LABELS.remediation, fallbackProfile: "MINIMAL", extra: { completion_ratio: completion } });
  }
  if (total > 0 && completion >= 0.5) {
    return publicProfileResult({ profile: "SUBSTANTIAL", labelMap: LABELS.remediation, fallbackProfile: "MINIMAL", extra: { completion_ratio: completion } });
  }
  if (complete > 0 || inProgress > 0) {
    return publicProfileResult({ profile: "IN_PROGRESS", labelMap: LABELS.remediation, fallbackProfile: "MINIMAL", extra: { completion_ratio: completion } });
  }
  return publicProfileResult({ profile: "MINIMAL", labelMap: LABELS.remediation, fallbackProfile: "MINIMAL", extra: { completion_ratio: completion } });
}

export function classifyLayer2Confidence({
  assessmentCompleteness = 0,
  evidenceCoverage = 0,
  remediationCompleteness = 0,
  missingInputPenalty = 0,
} = {}) {
  const assessment = clamp(assessmentCompleteness, 0, 1);
  const evidence = clamp(evidenceCoverage, 0, 1);
  const remediation = clamp(remediationCompleteness, 0, 1);
  const missingPenalty = clamp(missingInputPenalty, 0, 1);

  const score = clamp(
    assessment * CONFIDENCE_WEIGHTS.assessment_completeness +
      evidence * CONFIDENCE_WEIGHTS.evidence_coverage +
      remediation * CONFIDENCE_WEIGHTS.remediation_completeness +
      (1 - missingPenalty) * CONFIDENCE_WEIGHTS.data_quality,
    0,
    1
  );

  if (score >= 0.75) {
    return { profile: "HIGH", label: LABELS.confidence.HIGH, score };
  }
  if (score >= 0.45) {
    return { profile: "MODERATE", label: LABELS.confidence.MODERATE, score };
  }
  return { profile: "LOW", label: LABELS.confidence.LOW, score };
}

/**
 * Evidence gate used by the future overlay engine to prevent unsupported governance
 * claims from producing excessive reductions.
 */
export function evaluateGovernanceValidationGate({ governanceProfile, evidenceProfile } = {}) {
  const governance = upper(governanceProfile);
  const evidence = upper(evidenceProfile);

  const strongGovernance = governance === "ADVANCED" || governance === "ESTABLISHED";
  const strongEvidence = evidence === "AUDIT_READY" || evidence === "STRONG";

  if (strongGovernance && !strongEvidence) {
    return {
      passed: false,
      label: "Evidence support required",
      reason:
        "Strong stated governance maturity is not allowed to materially reduce exposure unless supported by strong evidence readiness.",
      max_reduction_ratio: LAYER_2_GUARDRAILS.max_reduction_without_strong_evidence_ratio,
    };
  }

  return {
    passed: true,
    label: "Validated",
    reason: "Governance maturity and evidence readiness are directionally aligned.",
    max_reduction_ratio: LAYER_2_GUARDRAILS.overall_adjustment_floor_ratio,
  };
}

export function rankExposureDrivers({
  governanceProfile,
  enforcementProfile,
  dataSensitivityProfile,
  userImpactProfile,
  evidenceProfile,
  remediationProfile,
  jurisdictionCount = 0,
  hasFallbackState = false,
  hasFederalOverlay = false,
  highRiskSector = false,
} = {}) {
  const drivers = [];
  const add = (id, weight, label) => drivers.push({ id, weight, label });

  if (upper(governanceProfile) === "WEAK" || upper(governanceProfile) === "DEVELOPING") {
    add("WEAK_GOVERNANCE", upper(governanceProfile) === "WEAK" ? 100 : 75, EXPOSURE_DRIVER_LABELS.WEAK_GOVERNANCE);
  }
  if (["VERY_HIGH", "HIGH"].includes(upper(enforcementProfile))) {
    add("HIGH_ENFORCEMENT", upper(enforcementProfile) === "VERY_HIGH" ? 90 : 75, EXPOSURE_DRIVER_LABELS.HIGH_ENFORCEMENT);
  }
  if (["CRITICAL", "HIGH"].includes(upper(dataSensitivityProfile))) {
    add("HIGH_DATA_SENSITIVITY", upper(dataSensitivityProfile) === "CRITICAL" ? 88 : 72, EXPOSURE_DRIVER_LABELS.HIGH_DATA_SENSITIVITY);
  }
  if (["ENTERPRISE_SCALE", "HIGH"].includes(upper(userImpactProfile))) {
    add("LARGE_USER_IMPACT", upper(userImpactProfile) === "ENTERPRISE_SCALE" ? 80 : 65, EXPOSURE_DRIVER_LABELS.LARGE_USER_IMPACT);
  }
  if (["LIMITED", "DEVELOPING"].includes(upper(evidenceProfile))) {
    add("LIMITED_EVIDENCE", upper(evidenceProfile) === "LIMITED" ? 78 : 55, EXPOSURE_DRIVER_LABELS.LIMITED_EVIDENCE);
  }
  if (["MINIMAL", "IN_PROGRESS"].includes(upper(remediationProfile))) {
    add("LIMITED_REMEDIATION", upper(remediationProfile) === "MINIMAL" ? 70 : 50, EXPOSURE_DRIVER_LABELS.LIMITED_REMEDIATION);
  }
  if (asNumber(jurisdictionCount, 0) > 1) add("MULTI_JURISDICTION", 45, EXPOSURE_DRIVER_LABELS.MULTI_JURISDICTION);
  if (hasFallbackState) add("FALLBACK_STATE", 40, EXPOSURE_DRIVER_LABELS.FALLBACK_STATE);
  if (hasFederalOverlay) add("FEDERAL_OVERLAY", 38, EXPOSURE_DRIVER_LABELS.FEDERAL_OVERLAY);
  if (highRiskSector) add("HIGH_RISK_SECTOR", 36, EXPOSURE_DRIVER_LABELS.HIGH_RISK_SECTOR);

  return drivers.sort((a, b) => b.weight - a.weight).slice(0, 5);
}

export function rankRiskReductionDrivers({ governanceProfile, evidenceProfile, remediationProfile, dataSensitivityProfile, userImpactProfile } = {}) {
  const drivers = [];
  const add = (id, weight, label) => drivers.push({ id, weight, label });

  if (["ADVANCED", "ESTABLISHED"].includes(upper(governanceProfile))) {
    add("STRONG_GOVERNANCE", upper(governanceProfile) === "ADVANCED" ? 90 : 70, RISK_REDUCTION_DRIVER_LABELS.STRONG_GOVERNANCE);
  }
  if (["AUDIT_READY", "STRONG"].includes(upper(evidenceProfile))) {
    add("STRONG_EVIDENCE", upper(evidenceProfile) === "AUDIT_READY" ? 88 : 72, RISK_REDUCTION_DRIVER_LABELS.STRONG_EVIDENCE);
  }
  if (["NEAR_COMPLETE", "SUBSTANTIAL"].includes(upper(remediationProfile))) {
    add("REMEDIATION_PROGRESS", upper(remediationProfile) === "NEAR_COMPLETE" ? 80 : 62, RISK_REDUCTION_DRIVER_LABELS.REMEDIATION_PROGRESS);
  }
  if (["STANDARD", "ELEVATED"].includes(upper(dataSensitivityProfile))) {
    add("LOWER_SENSITIVITY", upper(dataSensitivityProfile) === "STANDARD" ? 55 : 35, RISK_REDUCTION_DRIVER_LABELS.LOWER_SENSITIVITY);
  }
  if (["LOW", "MODERATE"].includes(upper(userImpactProfile))) {
    add("LOWER_IMPACT", upper(userImpactProfile) === "LOW" ? 50 : 30, RISK_REDUCTION_DRIVER_LABELS.LOWER_IMPACT);
  }
  if (upper(evidenceProfile) === "AUDIT_READY") {
    add("AUDIT_READY_EVIDENCE", 92, RISK_REDUCTION_DRIVER_LABELS.AUDIT_READY_EVIDENCE);
  }

  return drivers.sort((a, b) => b.weight - a.weight).slice(0, 5);
}

export function validateGravityFrameworkConfig() {
  const issues = [];
  const jurisdictionKeys = Object.keys(JURISDICTION_PROFILES);

  jurisdictionKeys.forEach((key) => {
    const row = JURISDICTION_PROFILES[key];
    if (!LABELS.enforcement[row.profile]) issues.push(`Invalid enforcement profile for ${key}: ${row.profile}`);
    if (!Number.isFinite(Number(row.internal_gravity_score))) issues.push(`Missing internal_gravity_score for ${key}`);
    if (Number.isFinite(Number(row.internal_gravity_score)) && (row.internal_gravity_score < 0 || row.internal_gravity_score > 100)) {
      issues.push(`Internal gravity score out of range for ${key}: ${row.internal_gravity_score}`);
    }
    if (!Number.isFinite(Number(row.internal_multiplier))) issues.push(`Missing internal_multiplier for ${key}`);
    if (Number.isFinite(Number(row.internal_multiplier)) && (row.internal_multiplier < LAYER_2_GUARDRAILS.gravity_only_min_ratio || row.internal_multiplier > LAYER_2_GUARDRAILS.gravity_only_max_ratio)) {
      issues.push(`Internal multiplier out of guardrail range for ${key}: ${row.internal_multiplier}`);
    }
    if (!row.rationale) issues.push(`Missing rationale for ${key}`);
    if (!row.source_basis) issues.push(`Missing source_basis for ${key}`);
    if (!row.confidence) issues.push(`Missing confidence for ${key}`);
  });

  Object.entries(FACTOR_WEIGHTS).forEach(([key, value]) => {
    if (!Number.isFinite(value) || value < 0) issues.push(`Invalid factor weight: ${key}`);
  });

  const factorWeightTotal = Object.values(FACTOR_WEIGHTS).reduce((a, b) => a + b, 0);
  if (Math.abs(factorWeightTotal - 1) > 0.00001) {
    issues.push(`Factor weights must total 1. Current total: ${factorWeightTotal}`);
  }

  const confidenceWeightTotal = Object.values(CONFIDENCE_WEIGHTS).reduce((a, b) => a + b, 0);
  if (Math.abs(confidenceWeightTotal - 1) > 0.00001) {
    issues.push(`Confidence weights must total 1. Current total: ${confidenceWeightTotal}`);
  }

  return {
    valid: issues.length === 0,
    issue_count: issues.length,
    issues,
    configured_jurisdictions_count: jurisdictionKeys.length,
    configured_jurisdictions: jurisdictionKeys.sort(),
    version: VERSION,
  };
}

export function getGravityFrameworkReferenceData() {
  return {
    version: VERSION,
    public_model_notice: PUBLIC_MODEL_NOTICE,
    labels: LABELS,
    configured_jurisdictions: Object.keys(JURISDICTION_PROFILES).sort(),
    jurisdiction_profiles: Object.keys(JURISDICTION_PROFILES)
      .sort()
      .reduce((acc, key) => {
        acc[key] = getJurisdictionEnforcementProfile(key);
        return acc;
      }, {}),
    factor_weights: getFactorWeights(),
    confidence_weights: getConfidenceWeights(),
    layer_2_guardrails: getLayer2Guardrails(),
    jurisdiction_gravity_policy:
      "Customer-facing outputs use enforcement labels only. Hidden internal gravity scores and multipliers are intentionally excluded from reference data and UI/PDF outputs.",
    exposure_driver_labels: EXPOSURE_DRIVER_LABELS,
    risk_reduction_driver_labels: RISK_REDUCTION_DRIVER_LABELS,
  };
}
